import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import api from "../../services/api";

// "Outros" é detectado pelo rótulo, normalizando acento e caixa — mesmo critério
// do cancelamento em AtendimentosEdit (isMotivoCancelamentoOutros).
const ehMotivoOutros = (motivo) => {
    const normalizado = String(motivo || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();

    return normalizado === "outros" || normalizado === "outro";
};

const PREFIXO_OUTROS = /^outros?\s*-\s*/i;

/**
 * Quebra o texto gravado em `atendimentos_relatos.motivo_cancelamento` de volta
 * nas duas partes que a tela edita.
 *
 * O campo é texto livre e acumula três formatos históricos:
 *   1. "Outros - <descrição>"  → opção "Outros" + descrição
 *   2. um rótulo da lista atual → opção correspondente
 *   3. um rótulo antigo, fora da lista atual, ou texto digitado à mão
 *      (o campo era um textarea antes do HNS-1438) → não casa com nada
 *
 * No caso 3 devolvemos `legado`, para a tela mostrar o valor atual e exigir
 * que o usuário escolha um motivo da lista vigente.
 */
const parseMotivoCancelamento = (valor, motivos = []) => {
    const texto = String(valor || "").trim();

    if (!texto) {
        return { motivo: "", outros: "", legado: "" };
    }

    if (PREFIXO_OUTROS.test(texto)) {
        const opcaoOutros = motivos.find(m => ehMotivoOutros(m)) || "Outros";
        return { motivo: opcaoOutros, outros: texto.replace(PREFIXO_OUTROS, "").trim(), legado: "" };
    }

    if (motivos.some(m => m === texto)) {
        return { motivo: texto, outros: "", legado: "" };
    }

    return { motivo: "", outros: "", legado: texto };
};

const ModalEditarMotivoCancelamento = ({ show, onHide, relato, motivos, onSalvo }) => {
    const [motivo, setMotivo] = useState("");
    const [outros, setOutros] = useState("");
    const [legado, setLegado] = useState("");
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");

    // Recarrega o formulário sempre que o modal abre para outro relato
    useEffect(() => {
        if (!show || !relato) return;

        const { motivo: motivoInicial, outros: outrosInicial, legado: legadoInicial } =
            parseMotivoCancelamento(relato.motivo_cancelamento, motivos);

        setMotivo(motivoInicial);
        setOutros(outrosInicial);
        setLegado(legadoInicial);
        setErro("");
    }, [show, relato, motivos]);

    const mostrarCampoOutros = ehMotivoOutros(motivo);
    const temListaMotivos = Array.isArray(motivos) && motivos.length > 0;

    const salvar = () => {
        const motivoLimpo = String(motivo || "").trim();
        const outrosLimpo = String(outros || "").trim();

        if (!motivoLimpo) {
            setErro("Selecione o motivo do cancelamento.");
            return;
        }

        if (ehMotivoOutros(motivoLimpo) && !outrosLimpo) {
            setErro("Descreva o motivo do cancelamento.");
            return;
        }

        // Mesmo formato gravado no cancelamento original, para o histórico e o
        // relatório continuarem lendo um único padrão de texto.
        const valorFinal = ehMotivoOutros(motivoLimpo)
            ? `Outros - ${outrosLimpo}`
            : motivoLimpo;

        setSalvando(true);
        setErro("");

        api.put(`/atendimentos/relatos/${relato.id}/`, { motivo_cancelamento: valorFinal })
            .then(() => {
                setSalvando(false);
                onSalvo(relato.id, valorFinal);
                onHide();
            })
            .catch(error => {
                console.error("Erro ao atualizar o motivo do cancelamento:", error);
                setSalvando(false);
                setErro("Não foi possível salvar o motivo. Tente novamente.");
            });
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Editar motivo do cancelamento</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {legado && (
                    <Alert variant="warning">
                        <strong>Motivo registrado anteriormente:</strong> {legado}
                        <div className="mt-1">
                            Esse texto não corresponde a nenhuma opção da lista atual. Selecione o motivo equivalente para o registro entrar nos relatórios.
                        </div>
                    </Alert>
                )}

                {temListaMotivos ? (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Motivo do Cancelamento <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="select"
                            name="motivo_cancelamento"
                            value={motivo}
                            onChange={e => setMotivo(e.target.value)}
                        >
                            <option value="">Selecione...</option>
                            {motivos.map((opcao, index) => (
                                <option key={`${opcao}-${index}`} value={opcao}>{opcao}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                ) : (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Motivo do Cancelamento <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="motivo_cancelamento"
                            value={motivo}
                            onChange={e => setMotivo(e.target.value)}
                        />
                    </Form.Group>
                )}

                {mostrarCampoOutros && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Descreva o motivo <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="motivo_cancelamento_outros"
                            value={outros}
                            onChange={e => setOutros(e.target.value)}
                        />
                    </Form.Group>
                )}

                {erro && <Alert variant="danger" className="mb-0">{erro}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide} disabled={salvando}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={salvar} disabled={salvando}>
                    {salvando ? <Spinner animation="border" size="sm" /> : "Salvar"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalEditarMotivoCancelamento.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    relato: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        motivo_cancelamento: PropTypes.string,
    }),
    motivos: PropTypes.arrayOf(PropTypes.string),
    onSalvo: PropTypes.func.isRequired,
};

ModalEditarMotivoCancelamento.defaultProps = {
    relato: null,
    motivos: [],
};

export default ModalEditarMotivoCancelamento;
